import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { GameAnnouncements, Popup } from "../types/global";
import Dialog from "./Dialog";
import Network, { BaseURL } from "../utils/Network";
import { Form } from "react-bootstrap";
import { useEventBus } from "../game-engine/hooks/useEventBusListener";
import { EventNames } from "../game-engine/utils/event-bus";
import { setPopupDisplay } from "../redux/announcements/action";
import { useAppTranslation } from "../i18n/useAppTranslation";
import { duePopups, popupDisplay } from "../utils/popup-schedule";

type Props = {};

// Every layout mounts this component, so what was shown is remembered for the
// whole visit here, not per mount: moving between pages never reopens a popup.
const sessionStartedAt = Date.now();
const shownThisSession = new Set<number>();
// Only the first mounted copy opens popups, so two layouts never both open one.
const mounted: symbol[] = [];

// For tests: start a new visit.
export const resetPopupSession = () => {
  shownThisSession.clear();
};

// The admin's popups (announcements of the Popup template). Each opens by itself
// at most once per visit and at most every POPUP_REOPEN_GAP_MS, up to its display
// count, and only once this visit's list has arrived from the server. An event
// banner can still open one on demand.
const Popups = (props: Props) => {
  const { t } = useAppTranslation("common");
  const { popups, displays, fetchedAt }: GameAnnouncements = useSelector(
    (state: any) => state.announcements
  );
  const userInfo = useSelector((state: any) => state.userInfo);
  const playerId: number | undefined = userInfo?.id;
  const axios = new Network();
  const [dialogData, setDialogData] = useState<Popup | null>(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const popupsRef = useRef<Popup[]>([]);
  const me = useRef(Symbol("popups"));
  const dispatch = useDispatch();
  const isPrimary = () => mounted[0] === me.current;

  useEffect(() => {
    const id = me.current;
    mounted.push(id);
    return () => {
      const index = mounted.indexOf(id);
      if (index >= 0) mounted.splice(index, 1);
    };
  }, []);

  useEffect(() => {
    popupsRef.current = popups;
  }, [popups]);

  useEventBus(EventNames.OpenPopup, null, (data) => {
    if (!data.id || !isPrimary()) return;
    const popup = popupsRef.current?.find((p) => p.id === data.id);
    if (popup) {
      setDialogData(popup);
      setDontShowAgain(Boolean(popup.dontShowAgain));
    }
  });

  useEventBus(EventNames.ClosePopup, null, () => {
    setDialogData(null);
  });

  const savePlayerAnnouncement = async (id: number) => {
    await axios.CallApi<Popup>({
      url: "/api/v2/announcement/saveplayerannouncement",
      method: "POST",
      customHost: BaseURL,
      body: {
        announcementId: id,
        dontShowAgain: true,
      },
    });
  };

  // Open the next due popup, if any. The list kept from the last visit does
  // not count: a popup added since would otherwise be missed on this load.
  useEffect(() => {
    if (dialogData || !playerId || !isPrimary()) return;
    if (!fetchedAt || fetchedAt < sessionStartedAt) return;
    const now = Date.now();
    const [next] = duePopups(popups, displays, playerId, now, shownThisSession);
    if (!next) return;
    shownThisSession.add(next.id);
    const shown = popupDisplay(displays, next.id, playerId);
    dispatch(
      setPopupDisplay({
        popupId: next.id,
        playerId,
        displayedCount: (shown?.displayedCount || 0) + 1,
        lastShownAt: now,
      })
    );
    setDontShowAgain(false);
    setDialogData(next);
  }, [popups, displays, fetchedAt, playerId, dialogData]);

  const handleClose = () => {
    if (!dialogData) return;
    // Store "Don't Show Again" on the server and right away here, so the
    // popup stays closed even before the list is read again.
    if (dontShowAgain) {
      savePlayerAnnouncement(dialogData.id);
      const shown = popupDisplay(displays, dialogData.id, playerId);
      dispatch(
        setPopupDisplay({
          popupId: dialogData.id,
          playerId,
          displayedCount: shown?.displayedCount || 0,
          lastShownAt: shown?.lastShownAt,
          dontShowAgain: true,
        })
      );
    }
    setDontShowAgain(false);
    // The next due popup, if any, opens from the effect above.
    setDialogData(null);
  };

  return (
    <Dialog
      open={Boolean(dialogData)}
      onClose={handleClose}
      title={dialogData?.title}
      size="lg"
      backdropClose
    >
      <div>
        {dialogData?.html && (
          <div dangerouslySetInnerHTML={{ __html: dialogData?.html }} />
        )}
        {dialogData?.hasDontShowOption && (
          <div
            style={{ marginBottom: -40 }}
            className="mt-3 w-100 d-flex   justify-content-center"
          >
            <label className="gap-2 cursor-pointer ">
              <Form.Check
                type="checkbox"
                label={t("common:actions.dontShowAgain")}
                checked={dontShowAgain}
                onClick={() => setDontShowAgain(!dontShowAgain)}
                className="form-checkbox"
              />
            </label>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default Popups;
